/* The generated ``FunctionSignatureOut`` type comes from a JSON-schema
 * codegen that doesn't carry full type info into TS — eslint's type-aware
 * rules consequently see the object's fields as ``any`` despite the
 * explicit interface. We narrow per-line where useful, but the
 * map/filter callbacks on the registry trip the rules collectively;
 * scoped to this file only because everything else is properly typed. */
/* eslint-disable @typescript-eslint/no-unsafe-assignment,
                  @typescript-eslint/no-unsafe-member-access,
                  @typescript-eslint/no-unsafe-call,
                  @typescript-eslint/no-unsafe-return */
/**
 * Hook powering the workflow-editor template autocomplete.
 *
 * Given the current TextField value + caret position, plus context about
 * the workflow (predecessor node ids, variable names, MCP trigger schema,
 * and the cached `functions.*` registry), it decides:
 *
 *   - whether the popup should be open (caret is inside `{{ … }}`),
 *   - what suggestions to show (filtered by the token under the caret),
 *   - how to apply a chosen suggestion (replaces just the token, not the
 *     whole input value).
 *
 * Scope: namespace roots + their first-level children —
 *   `functions.<name>`, `variables.<name>`, `trigger.<key>`,
 *   `nodes.<id>.output.<key>`. The output-key suggestions come from
 *   ``ctx.nodeOutputKeys`` (currently populated for MCP-trigger nodes;
 *   other node types fall back to "no schema known"). Walking deeper than
 *   one level past `.output.` is still v1.
 */

import { useMemo } from 'react'
import type { FunctionSignatureOut } from '../../../types/apiRequests'
import { useFunctionsRegistry } from './functionsRegistry'
import { findExpressionSpanAt, ExpressionSpan } from './findExpressionSpan'

export interface AutocompleteContext {
  /** IDs of nodes whose output the current node could read (predecessors). */
  availableNodeIds: string[]
  /** Workflow variable names — synced server-side from `{{ variables.X }}` refs. */
  variableNames: string[]
  /** Keys of `mcpToolInputSchema.properties` on the MCP trigger, if any. */
  triggerKeys: string[]
  /**
   * Top-level output keys per upstream node, used to suggest fields under
   * ``nodes.<id>.output.<partial>``. For MCP-trigger nodes these come from
   * ``mcpToolInputSchema.properties`` (trigger output mirrors trigger input
   * at runtime). Other node types are empty for now — v1 will add a static
   * shape map + live execution overlay.
   */
  nodeOutputKeys: Record<string, string[]>
  /**
   * Names declared by ancestor ``loop`` nodes (their ``itemVariable``).
   * Used by the validator to accept ``{{ item.foo }}`` inside a loop body
   * without false-flagging it as an unknown root. Autocomplete itself
   * doesn't suggest these yet — that's a future enhancement.
   */
  loopVariableNames: string[]
  /** Display metadata per node id; optional because validators don't need it. */
  nodeMeta?: Record<string, { label: string; type: string }>
}

export interface AutocompleteItem {
  /** Full token to insert (e.g. ``functions.uuid()``, ``trigger.x``). */
  insert: string
  /** Short header shown in the popup row. */
  label: string
  /** Subtitle / signature shown next to the label. */
  detail?: string
  /** Tooltip body — doc string or longer hint. */
  doc?: string
  /** Logical grouping for the section header in the popup. */
  group: 'functions' | 'variables' | 'trigger' | 'nodes' | 'roots'
}

export interface AutocompleteState {
  open: boolean
  span: ExpressionSpan | null
  items: AutocompleteItem[]
  /**
   * True when the editor tried to load ``functions.*`` signatures and the
   * fetch failed. Surfaced so the popper can show a one-line footer warning
   * instead of pretending the registry is just empty.
   */
  functionsLoadFailed: boolean
  /**
   * Apply ``item`` to ``value``: returns the new full text plus the new caret
   * position (right after the inserted token).
   */
  apply: (item: AutocompleteItem) => { value: string; caret: number }
}

const EMPTY_STATE: AutocompleteState = {
  open: false,
  span: null,
  items: [],
  functionsLoadFailed: false,
  apply: () => ({ value: '', caret: 0 }),
}

const NAMESPACE_ROOTS: AutocompleteItem[] = [
  {
    insert: 'functions.',
    label: 'functions.',
    detail: 'Built-in helpers (uuid, now, today, …)',
    group: 'roots',
  },
  {
    insert: 'variables.',
    label: 'variables.',
    detail: 'Link-level variables configured per organisation',
    group: 'roots',
  },
  {
    insert: 'trigger.',
    label: 'trigger.',
    detail: 'Input fields provided by the trigger / MCP caller',
    group: 'roots',
  },
  {
    insert: 'nodes.',
    label: 'nodes.',
    detail: 'Output of a preceding node in the graph',
    group: 'roots',
  },
]

export const useExpressionAutocomplete = (
  value: string,
  caret: number,
  ctx: AutocompleteContext,
): AutocompleteState => {
  const { functions, loadFailed } = useFunctionsRegistry()

  return useMemo<AutocompleteState>(() => {
    const span = findExpressionSpanAt(value, caret)
    if (!span) {
      return { ...EMPTY_STATE, functionsLoadFailed: loadFailed }
    }

    const items = buildItems(span.token, functions, ctx)

    const apply = (item: AutocompleteItem) => {
      const before = value.slice(0, span.tokenStart)
      const after = value.slice(span.tokenStart + span.token.length)
      const nextValue = before + item.insert + after
      const nextCaret = (before + item.insert).length
      return { value: nextValue, caret: nextCaret }
    }

    return {
      open: items.length > 0,
      span,
      items,
      functionsLoadFailed: loadFailed,
      apply,
    }
  }, [value, caret, ctx, functions, loadFailed])
}

const buildItems = (
  token: string,
  functions: FunctionSignatureOut[],
  ctx: AutocompleteContext,
): AutocompleteItem[] => {
  const tokenLower = token.toLowerCase()

  // Decide which root we're inside. The token is the dot-path under the
  // caret. If it contains no `.`, we're picking a root namespace. If it
  // starts with one of the known roots, we suggest children of that root.
  const dotIdx = token.indexOf('.')
  if (dotIdx === -1) {
    return NAMESPACE_ROOTS.filter((r) => r.label.toLowerCase().startsWith(tokenLower))
  }

  const root = token.slice(0, dotIdx)
  const rest = token.slice(dotIdx + 1)
  const restLower = rest.toLowerCase()

  if (root === 'functions') {
    return functions
      .filter((f: FunctionSignatureOut) => f.name.toLowerCase().startsWith(restLower))
      .map(
        (f: FunctionSignatureOut): AutocompleteItem => ({
          insert: `functions.${f.name}(${argPlaceholder(f)})`,
          label: `functions.${f.name}`,
          detail: f.signature.replace(/^[^(]+/, ''),
          doc: f.doc ?? undefined,
          group: 'functions',
        }),
      )
  }

  if (root === 'variables') {
    return ctx.variableNames
      .filter((n: string) => n.toLowerCase().startsWith(restLower))
      .map(
        (name: string): AutocompleteItem => ({
          insert: `variables.${name}`,
          label: `variables.${name}`,
          group: 'variables',
        }),
      )
  }

  if (root === 'trigger') {
    return ctx.triggerKeys
      .filter((k: string) => k.toLowerCase().startsWith(restLower))
      .map(
        (key: string): AutocompleteItem => ({
          insert: `trigger.${key}`,
          label: `trigger.${key}`,
          group: 'trigger',
        }),
      )
  }

  if (root === 'nodes') {
    const subDot = rest.indexOf('.')
    if (subDot === -1) {
      return ctx.availableNodeIds
        .filter((id: string) => id.toLowerCase().startsWith(restLower))
        .map(
          (id: string): AutocompleteItem => ({
            insert: `nodes.${id}.output.`,
            label: `nodes.${id}`,
            detail: 'preceding node — pick a field after .output.',
            group: 'nodes',
          }),
        )
    }
    const nodeId = rest.slice(0, subDot)
    const tail = rest.slice(subDot + 1)
    const tailDot = tail.indexOf('.')

    // Author has typed `nodes.<id>.<partial-of-"output">` — finish the
    // `.output.` segment before drilling further.
    if (tailDot === -1) {
      if (tail === 'output') {
        // Caret sits right after `output` (no trailing dot yet) — drop the
        // dot suggestion in so a single Enter lands us at `output.`.
        return [
          {
            insert: `nodes.${nodeId}.output.`,
            label: `nodes.${nodeId}.output`,
            detail: 'pick a field below',
            group: 'nodes',
          },
        ]
      }
      if ('output'.startsWith(tail.toLowerCase())) {
        return [
          {
            insert: `nodes.${nodeId}.output.`,
            label: `nodes.${nodeId}.output`,
            detail: 'pick a field below',
            group: 'nodes',
          },
        ]
      }
      return []
    }

    // Author is inside `nodes.<id>.output.<fieldPartial>` — suggest the
    // node's known top-level output keys. Currently only MCP-trigger
    // nodes contribute (their output mirrors the trigger input schema);
    // other node types yield an empty list and the popup closes.
    const segment = tail.slice(0, tailDot)
    const fieldPartial = tail.slice(tailDot + 1).toLowerCase()
    if (segment !== 'output') return []
    // eslint-disable-next-line security/detect-object-injection
    const keys = ctx.nodeOutputKeys[nodeId] ?? []
    return keys
      .filter((k: string) => k.toLowerCase().startsWith(fieldPartial))
      .map(
        (key: string): AutocompleteItem => ({
          insert: `nodes.${nodeId}.output.${key}`,
          label: `nodes.${nodeId}.output.${key}`,
          detail: 'upstream node output',
          group: 'nodes',
        }),
      )
  }

  return []
}

/**
 * Render the placeholder arguments for a function-call insertion. We emit
 * just `()` for parameterless callees and `(<arg>, …)` with default values
 * stubbed in for the rest, so the author sees the shape immediately.
 */
const argPlaceholder = (fn: FunctionSignatureOut): string => {
  if (!fn.params.length) return ''
  return fn.params
    .map((p) => (p.default !== null && p.default !== undefined ? p.default : `<${p.name}>`))
    .join(', ')
}
